import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartDiscountErrorGeneric - The recoverable request error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  static observedAttributes = ['hidden'];

  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping', 'cartDiscountErrorGeneric'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.removeAttribute('hidden');
  }

  attributeChangedCallback(name, _oldValue, newValue) {
    if (name === 'hidden' && newValue !== null && this.isConnected) {
      this.removeAttribute('hidden');
    }
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping, cartDiscountErrorGeneric } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;
    const selectionStart = discountCode.selectionStart;
    const selectionEnd = discountCode.selectionEnd;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      cartDiscountErrorGeneric.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      if (!response.ok || !data || typeof data !== 'object' || !Array.isArray(data.discount_codes)) {
        throw new Error(Theme.translations.cart_update_error);
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string' || newHtml.trim() === '') {
        throw new Error(Theme.translations.cart_update_error);
      }

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        this.#handleDiscountError('discount_code');
        discountCode.focus();
        return;
      }

      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      if (!section) throw new Error(Theme.translations.cart_update_error);

      const discountCodes = section.querySelectorAll('.cart-discount__pill');
      const codes = Array.from(discountCodes)
        .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
        .filter(Boolean);
      // Before morphing, we need to check if the shipping discount is applicable in the UI
      // we check the liquid logic compared to the cart payload to assess whether we leveraged
      // a valid shipping discount code.
      if (
        codes.length === existingDiscounts.length &&
        codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === true;
        })
      ) {
        this.#handleDiscountError('shipping');
        discountCode.focus();
        return;
      }

      await morphSection(this.dataset.sectionId, newHtml);
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
    } catch (error) {
      if (error?.name === 'AbortError') return;
      cartDiscountError.classList.remove('hidden');
      cartDiscountErrorGeneric.textContent = Theme.translations.cart_update_error;
      cartDiscountErrorGeneric.classList.remove('hidden');
      discountCode.focus();
      if (selectionStart !== null && selectionEnd !== null && typeof discountCode.setSelectionRange === 'function') {
        discountCode.setSelectionRange(selectionStart, selectionEnd);
      }
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      const newHtml = data?.sections?.[this.dataset.sectionId];
      if (!response.ok || !data || typeof data !== 'object' || typeof newHtml !== 'string' || newHtml.trim() === '') {
        throw new Error(Theme.translations.cart_update_error);
      }

      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      if (!parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`)) {
        throw new Error(Theme.translations.cart_update_error);
      }

      await morphSection(this.dataset.sectionId, newHtml);
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
    } catch (error) {
      if (error?.name === 'AbortError') return;
      const genericError = this.refs.cartDiscountErrorGeneric;
      this.refs.cartDiscountError.classList.remove('hidden');
      genericError.textContent = Theme.translations.cart_update_error;
      genericError.classList.remove('hidden');
      if (event.target instanceof HTMLElement) event.target.focus();
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
